/**
 * Checks if a member is a learner based on their role
 * @param {Object} member - The member to check
 * @returns {boolean} - True if the member is a learner, false otherwise
 */
export const isLearner = (member) => {
  return member.type === 'person' && member.role && member.role.toLowerCase().includes('learner');
};

/**
 * Checks if a member is an attorney based on their role
 * @param {Object} member - The member to check
 * @returns {boolean} - True if the member is an attorney, false otherwise
 */
export const isAttorney = (member) => {
  return member.type === 'person' && member.role && member.role.toLowerCase().includes('attorney');
};

/**
 * Gets all learners from the selected members array
 * @param {Array} selectedMembers - Array of selected members
 * @returns {Array} - Array of learner members
 */
export const getLearners = (selectedMembers) => {
  return selectedMembers.filter(isLearner);
};

/**
 * Gets all attorneys from the selected members array
 * @param {Array} selectedMembers - Array of selected members
 * @returns {Array} - Array of attorney members
 */
export const getAttorneys = (selectedMembers) => {
  return selectedMembers.filter(isAttorney);
};

/**
 * Validates that exactly one learner and one attorney are selected
 * @param {Array} selectedMembers - Array of selected members
 * @returns {Object} - Validation result with isValid flag and error message
 */
export const validateAttorneyLearnerSelection = (selectedMembers) => {
  if (selectedMembers.length === 0) {
    return {
      isValid: false,
      error: 'Please select one learner and one attorney',
    };
  }

  const learners = getLearners(selectedMembers);
  const attorneys = getAttorneys(selectedMembers);

  // Must have exactly one learner and one attorney (total 2 participants)
  if (learners.length !== 1 || attorneys.length !== 1 || selectedMembers.length !== 2) {
    return {
      isValid: false,
      error: 'Please select exactly one learner and one attorney',
      toastMessage: 'You must select exactly one learner and one attorney to create a conversation',
    };
  }

  return {
    isValid: true,
    error: '',
  };
};

/**
 * Checks if the selection is valid for creating a conversation
 * (exactly one learner and one attorney)
 * @param {Array} selectedMembers - Array of selected members
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidSelectionForConversation = (selectedMembers) => {
  const validation = validateAttorneyLearnerSelection(selectedMembers);
  return validation.isValid;
};

